import { useCallback, useEffect, useRef, useState } from 'react';
import { isDesktop } from '@/lib/fileSystem';
import { clearDepsCache } from '@/lib/nativebridge';

/**
 * React wrapper over the desktop "offline engine pack" IPC
 * (preload: enginePackStatus / installEnginePack / onEnginePackProgress).
 *
 * The pack is the opt-in download of the native engines (compress/OCR/convert/
 * unlock/flatten) for offline use. On the web — or any build without the IPC —
 * the hook is inert (`supported: false`) so callers can render nothing.
 *
 * Exposes:
 *   status      { installed, installedVersion, availableVersion, updateAvailable } | null
 *   loading     true while the initial/refresh status probe is in flight
 *   installing  true while a download+extract is running
 *   progress    { phase: 'download'|'extract'|'done', percent } | null
 *   error       last install/status error message | null
 *   install()   kicks off download+extract, streaming progress
 *   refresh()   re-probes status
 *   supported   whether the offline pack IPC is available (desktop build)
 */
export function useEnginePack() {
  const supported = isDesktop() && typeof window !== 'undefined' && !!window.electronAPI?.enginePackStatus;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(supported);
  const [installing, setInstalling] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const s = await window.electronAPI.enginePackStatus();
      if (mounted.current) setStatus(s);
    } catch (e) {
      if (mounted.current) setError(e?.message || 'Could not check offline mode');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const install = useCallback(async () => {
    if (!supported || installing) return;
    setError(null);
    setInstalling(true);
    setProgress({ phase: 'download', percent: 0 });
    const off = window.electronAPI.onEnginePackProgress?.((p) => {
      if (mounted.current) setProgress(p);
    });
    try {
      await window.electronAPI.installEnginePack();
      // hasNative() reads a cached deps probe — clear it so the next heavy op
      // sees the freshly-extracted engines without an app restart, regardless
      // of which surface (Account card or contextual prompt) triggered this.
      clearDepsCache();
      await refresh();
    } catch (e) {
      if (mounted.current) setError(e?.message || 'Download failed. Check your connection and try again.');
    } finally {
      if (off) off();
      if (mounted.current) {
        setInstalling(false);
        setProgress(null);
      }
    }
  }, [supported, installing, refresh]);

  return { status, loading, installing, progress, error, install, refresh, supported };
}
